/**
 * The Morning Notes add-on rows, named once.
 *
 * ⚠️ Why this is its own module: Settings shows "{n} of {total}" for Customized
 * Morning Notes and used to hand-list five keys beside a literal "of 5". That
 * is the drift the DELIVERY_FEATURES comment in Settings warns about (#109),
 * and #1898's sixth add-on walked straight into it: six toggles on would have
 * read "5 of 5" with nothing failing. Both numbers come from this array now.
 *
 * It lives in src/lib rather than in BriefingBuilder.tsx so vitest and the
 * kin-chat honesty prompt (#1899) can read it without pulling in the supabase
 * client through React.
 *
 * Add a row to BriefingBuilder, add its key here, and the summary follows on
 * its own. Keys match the BriefingPrefs interface exactly, proved in both
 * directions by briefingAddonKeys.test.ts (a TypeScript interface has no
 * runtime keys to compare against, so the test reads the source).
 */
export const BRIEFING_ADDON_KEYS = [
  "weather",
  "news",
  "stocks",
  "movement",
  "faith",
  "dinner",
] as const;

export type BriefingAddonKey = (typeof BRIEFING_ADDON_KEYS)[number];

/** Weather is the one add-on a free account can switch on (#533). */
export const BRIEFING_FREE_ADDON_KEYS: readonly BriefingAddonKey[] = ["weather"];

/**
 * Is this add-on switched on *for this user*? A Pro-only row left saved as
 * `on` by an account that later lapsed must not count, which is why the tier
 * is part of the question rather than something each caller remembers to
 * apply on its own.
 */
export function isBriefingAddonOn(
  prefs: Record<string, { on?: boolean } | undefined> | null | undefined,
  key: BriefingAddonKey,
  isPro: boolean,
): boolean {
  if (!prefs?.[key]?.on) return false;
  return BRIEFING_FREE_ADDON_KEYS.includes(key) || isPro;
}
